import Vue from 'vue'
import to from 'await-to-js'
import Axios from '@/config/axios'
import buildClient from '@/api/client'
import checkIfUserExistsQuery from '@/api/user-exists.gql'
import getCurrentUserQuery from '@/api/account'
import getSavedPaymentsQuery from '@/api/account-saved-payments'
import deleteSavedPaymentQuery from '@/api/delete-saved-payment'

const FETCH_STATES = {
  initial: 0,
  fetching: 1,
  done: 2,
}
let userFetchState = FETCH_STATES.initial
let savedPaymentsFetchState = FETCH_STATES.initial

const getDefaultState = () => {
  return {
    currentUser: null,
    hasFetchedCurrentUser: false,
    paths: {},
  }
}

export default {
  namespaced: true,
  state: getDefaultState(),

  mutations: {
    setState(state, payload) {
      state[payload[0]] = payload[1]
    },
    setInitialState(state) {
      const defaultState = getDefaultState()
      for (const key in defaultState) {
        state[key] = defaultState[key]
      }
    },
    setUserData(state, payload) {
      state.currentUser = payload ? { ...state.currentUser, ...payload } : payload
      state.hasFetchedCurrentUser = true
    },

    setAccountPaths(state, paths) {
      state.paths = { ...state.paths, ...paths }
    },

    pushToAddresses(state, addressObject) {
      state.currentUser.addresses.push(addressObject)
    },

    deleteAddress(state, addressIndex) {
      state.currentUser.addresses.splice(addressIndex, 1)
    },

    setAddress(state, payload) {
      Vue.set(state.currentUser.addresses, payload[0], payload[1])
    },

    setAddressIsDefault(state, payload) {
      const address = state.currentUser.addresses[payload[0]]
      Vue.set(address, 'is_default', payload[1])
    },

    setSavedPayments(state, savedPayments) {
      Vue.set(state.currentUser, 'savedPayments', savedPayments || [])
      const orderedSavedPayments = savedPayments.sort(payment => Date.parse(payment.createdAt))
      Vue.set(state, 'orderedSavedPayments', orderedSavedPayments)
    },
  },

  actions: {
    async fetchData({ dispatch }) {
      if (userFetchState !== FETCH_STATES.initial) return

      userFetchState = FETCH_STATES.fetching

      const { currentUser, paths } = await buildClient().query(getCurrentUserQuery())

      dispatch('handleFetchData', { currentUser, paths })
    },

    async fetchSavedPayments({ commit }) {
      if (savedPaymentsFetchState !== FETCH_STATES.initial) return

      savedPaymentsFetchState = FETCH_STATES.fetching

      const { currentUser } = await buildClient().query(getSavedPaymentsQuery())

      commit('setSavedPayments', currentUser.savedPayments)

      savedPaymentsFetchState = FETCH_STATES.done
    },

    handleFetchData({ commit }, { currentUser, paths }) {
      commit('setUserData', currentUser)
      commit('setAccountPaths', paths)
      userFetchState = FETCH_STATES.done
    },

    createAddress({ commit, dispatch }, addressObject) {
      dispatch('updateIfAddressExists', addressObject).then(addressExists => {
        if (!addressExists) {
          if (addressObject.is_default) {
            dispatch('resetDefaultAddressFlag')
          }
          commit('pushToAddresses', addressObject)
        }
      })
    },

    updateIfAddressExists({ dispatch, state }, addressObject) {
      return new Promise(resolve => {
        const addressExists = state.currentUser.addresses.some(
          address => address.id == addressObject.id
        )

        if (addressExists) {
          dispatch('updateAddress', addressObject)
        }
        resolve(addressExists)
      })
    },

    updateAddress({ commit, dispatch, state }, addressObject) {
      const addressIndex = state.currentUser.addresses.findIndex(
        address => address.id == addressObject.id
      )

      if (addressObject.is_default) {
        dispatch('resetDefaultAddressFlag', addressIndex)
      }
      commit('setAddress', [addressIndex, addressObject])
    },

    resetDefaultAddressFlag({ commit, state }, defaultAddressIndex) {
      state.currentUser.addresses.forEach((address, i) => {
        if (i != defaultAddressIndex) {
          commit('setAddressIsDefault', [i, false])
        }
      })
    },

    deleteSavedPayment({ commit, state }, paymentReference) {
      buildClient()
        .query(deleteSavedPaymentQuery(paymentReference))
        .then(({ deleteSavedPayment: { success } }) => {
          if (success) {
            const newSavedPayments = state.currentUser.savedPayments.filter(
              payment => payment.reference !== paymentReference
            )
            commit('setSavedPayments', newSavedPayments)
          }
        })
        // Close opened confirmation modal
        .finally(() => $.featherlight.current().close())
    },

    async isUserAccount({ dispatch }, email) {
      const [error] = await to(dispatch('getUserByEmail', email))
      return !error
    },

    getUserByEmail({ commit }, email) {
      return buildClient()
        .query(checkIfUserExistsQuery, { email })
        .then(({ userExists, paths }) => {
          commit('setAccountPaths', paths)
          return userExists || Promise.reject(new Error('No user available for given email'))
        })
    },

    async loginUser({ dispatch, state }, { email, password, rememberMe }) {
      dispatch('order/updateUserEmail', email, { root: true })
      const data = {
        spree_user: {
          email,
          password,
          remember_me: rememberMe,
        },
      }
      const response = await Axios.post(state.paths.login, data)
      userFetchState = FETCH_STATES.initial
      await dispatch('fetchData')
      return response
    },
  },

  getters: {
    loggedIn: state => !!state.currentUser,
    currentUser: state => state.currentUser,
    hasFetchedCurrentUser: state => state.hasFetchedCurrentUser,
    firstName: state => state.currentUser?.first_name || '',
    orderedSavedPayments: state => state.orderedSavedPayments || [],
    paths: state => state.paths,
  },
}
